import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { NAV, ROLE_LEVEL, APP_LOGO } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Menu, X, LogOut, Search, Bell, Home, Gem, ListTodo, Heart, MoreHorizontal, KeyRound } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const [cpOpen, setCpOpen] = useState(false);
  const level = ROLE_LEVEL[user?.role] || 0;
  const items = NAV.filter((n) => !n.minLevel || level >= n.minLevel);
  return (
    <div className="flex flex-col h-full bg-paper">
      <div className="px-4 py-5 border-b border-gold/20 flex justify-center">
        <img src={APP_LOGO} alt="Primrose Organizer" className="h-20 w-auto object-contain" data-testid="sidebar-logo" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 group",
                isActive ? "bg-burgundy text-white shadow-sm" : "text-[#4A4A4A] hover:bg-beige hover:text-burgundy"
              )
            }
          >
            <n.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-gold/20">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="w-9 h-9 border border-gold/30">
            <AvatarFallback className="bg-beige text-burgundy text-xs font-semibold">
              {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1A1A1A] truncate">{user?.name}</p>
            <p className="text-[11px] text-gold-dark capitalize font-medium">{user?.role}</p>
          </div>
          <button onClick={() => setCpOpen(true)} data-testid="change-password-btn" aria-label="Ganti Password" className="p-2 rounded-lg text-[#7A7A7A] hover:text-burgundy hover:bg-beige transition-colors">
            <KeyRound className="w-4 h-4" />
          </button>
          <button onClick={logout} data-testid="logout-btn" aria-label="Logout" className="p-2 rounded-lg text-[#7A7A7A] hover:text-burgundy hover:bg-beige transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
    </div>
  );
}

const MOBILE_NAV = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/events", label: "Events", icon: Gem },
  { to: "/activity", label: "Activity", icon: ListTodo },
  { to: "/clients", label: "Clients", icon: Heart },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 border-r border-gold/20 z-30">
        <SidebarContent />
      </aside>

      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        {/* Header */}
        <header className="glass-header sticky top-0 z-20 h-16 flex items-center gap-3 px-4 lg:px-8">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-lg hover:bg-beige" data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="w-5 h-5 text-burgundy" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <img src={APP_LOGO} alt="Primrose Organizer" className="lg:hidden h-9 w-auto object-contain" data-testid="header-logo" />

          <button
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-trigger"
            className="flex items-center gap-2 flex-1 max-w-md px-4 py-2 rounded-full bg-white border border-gold/30 text-[#7A7A7A] hover:border-burgundy/40 transition-colors text-sm"
          >
            <Search className="w-4 h-4" />
            <span>Cari client, event, vendor...</span>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6 pb-24 lg:pb-6 max-w-[1400px] w-full mx-auto">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-paper border-t border-gold/20 flex items-center justify-around h-16 px-2">
          {MOBILE_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn("flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-[10px] font-medium", isActive ? "text-burgundy" : "text-[#7A7A7A]")
              }
            >
              <n.icon className="w-5 h-5" strokeWidth={1.6} />
              {n.label}
            </NavLink>
          ))}
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-nav-more" className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-medium text-[#7A7A7A]">
            <MoreHorizontal className="w-5 h-5" />
            More
          </button>
        </nav>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} onNavigate={(path) => { setSearchOpen(false); navigate(path); }} />
    </div>
  );
}
